import { useFcmSubscribe, useFcmUnsubscribe } from "@/tanstack-hooks";
import { useEffect, useState } from "react";
import { useToast } from "./use-toast";
import type {
  NotificationReceivedEvent,
  TokenReceivedEvent,
} from "@capacitor-firebase/messaging";
import { Capacitor } from "@capacitor/core";
import { AppPlatformType } from "@/generated-api/models/AppPlatformType";
import type {
  NormalizedPushSubscription,
  UsePushNotificationsResult,
} from "./usePushNotifications.types";

const fcmEndpoint = (token: string) => `fcm://${token}`;

const currentPlatform = (): AppPlatformType | null => {
  const p = Capacitor.getPlatform();
  if (p === "ios") return AppPlatformType.IOS;
  if (p === "android") return AppPlatformType.ANDROID;
  return null;
};

const mapPermission = (
  raw: "prompt" | "prompt-with-rationale" | "granted" | "denied"
): NotificationPermission => {
  if (raw === "granted") return "granted";
  if (raw === "denied") return "denied";
  return "default";
};

export const useFcmPushNotifications = (): UsePushNotificationsResult => {
  const { toast } = useToast();
  const [permission, setPermission] =
    useState<NotificationPermission>("default");
  const [subscription, setSubscription] =
    useState<NormalizedPushSubscription | null>(null);
  const [error, setError] = useState<string | null>(null);
  // On web, we never run the async init below — but the dispatcher always
  // calls this hook for hook-order stability, so callers see the result.
  // Mark initialized synchronously on web so consumers don't wait forever.
  const [isInitialized, setIsInitialized] = useState(
    () => !Capacitor.isNativePlatform()
  );
  const fcmSubscribeMutation = useFcmSubscribe();
  const fcmUnsubscribeMutation = useFcmUnsubscribe();

  useEffect(() => {
    // Skip the entire FCM plugin init on web. The dispatcher in
    // usePushNotifications.ts returns the VAPID hook's result on web, so this
    // hook's state is never consumed there — and the @capacitor-firebase/messaging
    // web fallback would try to load Firebase JS SDK (stubbed at build time).
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    let cleanup: (() => void) | undefined;

    void (async () => {
      try {
        const [{ FirebaseMessaging }, { Badge }, { App }] = await Promise.all([
          import("@capacitor-firebase/messaging"),
          import("@capawesome/capacitor-badge"),
          import("@capacitor/app"),
        ]);

        // iOS sets the badge from aps.badge on the push payload but never
        // decrements it. Clear it (and the notification center) whenever the
        // app becomes active — initial mount, return from background, and on
        // explicit notification tap.
        const clearBadge = async () => {
          try {
            // On iOS, Badge.clear() internally calls requestPermissions(.badge)
            // (UNUserNotificationCenter.requestAuthorization), which surfaces the
            // system notification prompt. Guard it behind an existing grant so we
            // never prompt before the user opts in via our EnableNotificationsDialog.
            // checkPermissions() only reads status — it never prompts — and an
            // un-granted user has no badge to clear anyway.
            const { receive } = await FirebaseMessaging.checkPermissions();
            if (receive !== "granted") return;
            await Badge.clear();
            await FirebaseMessaging.removeAllDeliveredNotifications();
          } catch (err) {
            console.warn("Failed to clear badge:", err);
          }
        };

        const status = await FirebaseMessaging.checkPermissions();
        if (cancelled) return;
        setPermission(mapPermission(status.receive));
        setIsInitialized(true);

        // Token rotation — Firebase re-emits when the token changes.
        // We send the fresh token to backend so subsequent sends reach the device.
        const tokenHandle = await FirebaseMessaging.addListener(
          "tokenReceived",
          (event: TokenReceivedEvent) => {
            const endpoint = fcmEndpoint(event.token);
            setSubscription({ endpoint });
            fcmSubscribeMutation.mutate({
              fcm_token: event.token,
              platform: currentPlatform(),
            });
          }
        );

        const receivedHandle = await FirebaseMessaging.addListener(
          "notificationReceived",
          (event: NotificationReceivedEvent) => {
            // Foreground delivery — iOS/Android suppress the system banner.
            // Surface as an in-app toast so the user still sees it.
            toast({
              title: event.notification.title ?? "New notification",
              description: event.notification.body ?? "",
              className: "dark",
            });
          }
        );

        // User tapped a notification from lock screen / notification center.
        const actionHandle = await FirebaseMessaging.addListener(
          "notificationActionPerformed",
          () => {
            void clearBadge();
          }
        );

        // App returned to foreground from background — no re-mount happens
        // in the SPA, so we need an explicit listener.
        const appStateHandle = await App.addListener(
          "appStateChange",
          (state) => {
            if (state.isActive) void clearBadge();
          }
        );

        // Initial mount (cold start / first navigation to a screen using this
        // hook). Also covers the case where a tap launched the app.
        void clearBadge();

        // If permission was previously granted, fetch the current token so
        // the backend stays in sync (tokens can rotate between launches).
        if (status.receive === "granted") {
          const { token } = await FirebaseMessaging.getToken();
          if (!cancelled && token) {
            setSubscription({ endpoint: fcmEndpoint(token) });
          }
        }

        cleanup = () => {
          void tokenHandle.remove();
          void receivedHandle.remove();
          void actionHandle.remove();
          void appStateHandle.remove();
        };
      } catch (err) {
        console.error("Failed to initialise push notifications:", err);
        if (!cancelled) {
          setError("Could not initialise push notifications.");
          setIsInitialized(true); // unblock UI gates even on failure
        }
      }
    })();

    return () => {
      cancelled = true;
      cleanup?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast]);

  const requestNotificationPermission = async () => {
    try {
      const { FirebaseMessaging } = await import(
        "@capacitor-firebase/messaging"
      );
      const result = await FirebaseMessaging.requestPermissions();
      setPermission(mapPermission(result.receive));

      if (result.receive !== "granted") {
        setError("Notification permission denied.");
        return;
      }

      const { token } = await FirebaseMessaging.getToken();
      if (!token) {
        setError("Could not obtain FCM token.");
        return;
      }

      setSubscription({ endpoint: fcmEndpoint(token) });
      fcmSubscribeMutation.mutate(
        { fcm_token: token, platform: currentPlatform() },
        {
          onError: (err) => {
            console.error("Could not register FCM token with backend", err);
            toast({
              variant: "destructive",
              title: "Could not enable notifications",
              description: "Try again from settings.",
              className: "dark",
            });
          },
        }
      );
    } catch (err) {
      const error = err as Error;
      setError("Failed to request notification permission: " + error.message);
    }
  };

  const unsubscribeFromPush = async () => {
    if (!subscription) return;
    const token = subscription.endpoint.replace(/^fcm:\/\//, "");
    fcmUnsubscribeMutation.mutate({
      fcm_token: token,
      platform: currentPlatform(),
    });

    try {
      const { FirebaseMessaging } = await import(
        "@capacitor-firebase/messaging"
      );
      // Invalidates the token on Firebase's side; a new one will be issued on
      // the next requestNotificationPermission() / getToken() call.
      await FirebaseMessaging.deleteToken();
    } catch (err) {
      console.error("deleteToken failed:", err);
    }

    setSubscription(null);
    // Note: this clears subscription but doesn't change `permission`. To go
    // from "granted" → "denied" the user must disable in OS Settings.
  };

  return {
    isInitialized,
    isSupported: true,
    permission,
    subscription,
    error,
    requestNotificationPermission,
    unsubscribeFromPush,
  };
};
